import { mergeScorecardHalves } from 'app/pages/tpoPortal/commandCenterHome/CommandCenterHome/fileQualityScorecard.utils';
import {
  brokerQualityScorecardLoansApi,
  brokerQualityScorecardUnderwritingApi,
} from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { CommandCenterLoadKey, markCommandCenterLoadedAction } from './loadStatus.actions';

/**
 * File-quality scorecard read (DEV-3777). The zone is DARK until a backend
 * feature toggle is on: when off, both endpoints 404. We fetch each half
 * independently and swallow ANY per-half error (404 flag-off, 403 broker-auth
 * gap, network) to null so the zone degrades to "not available" without surfacing
 * an error toast or the global forbidden page-reload loop. The two non-null
 * halves are then merged; if both are null the slice holds its empty state and
 * the zone is hidden.
 */
const SKIP_FORBIDDEN_CONFIG = { skipResponseInterceptor: { forbiddenError: true } };

export const COMMAND_CENTER_FILE_QUALITY_ACTION_TYPE = createNamedActions(
  'commandCenterFileQuality',
  {
    SET_FILE_QUALITY_SCORECARD: 'SET_FILE_QUALITY_SCORECARD',
  },
);

// Resolves one half to its response data, or null on any failure (flag-off 404,
// 403, network) — the zone must hide rather than error when a half is absent.
const fetchHalf = async request => {
  try {
    const { data } = await request();

    return data ?? null;
  } catch {
    return null;
  }
};

export const getFileQualityScorecardAction = catchAsyncActionError(() => async dispatch => {
  try {
    const [underwriting, loans] = await Promise.all([
      fetchHalf(() =>
        brokerQualityScorecardUnderwritingApi.getQualityScorecard({
          config: SKIP_FORBIDDEN_CONFIG,
        }),
      ),
      fetchHalf(() =>
        brokerQualityScorecardLoansApi.getQualityScorecardMetrics({
          config: SKIP_FORBIDDEN_CONFIG,
        }),
      ),
    ]);

    dispatch({
      type: COMMAND_CENTER_FILE_QUALITY_ACTION_TYPE.SET_FILE_QUALITY_SCORECARD,
      payload: mergeScorecardHalves({ underwriting, loans }),
    });
  } finally {
    // In a `finally` so the slice is marked loaded even when both halves 404/403
    // and merge to null — otherwise the zone skeleton never clears.
    dispatch(markCommandCenterLoadedAction(CommandCenterLoadKey.FILE_QUALITY));
  }
});
